// src/redux/partnerSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCookie } from '../utils/cookies';

const API = process.env.REACT_APP_API_URL;
const hdr = (token) => ({
  'Content-Type': 'application/json',
  'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
  Authorization: token ? `Bearer ${token}` : undefined,
});
const gt = (t) => t.getState().auth.user?.token;

// ── Profile ──
export const fetchPartnerProfile = createAsyncThunk('partner/profile', async (_, t) => {
  const r = await fetch(`${API}/api/partner/profile`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const updatePartnerProfile = createAsyncThunk('partner/updateProfile', async (updates, t) => {
  const r = await fetch(`${API}/api/partner/profile`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(updates) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Avatar ──
export const uploadPartnerAvatar = createAsyncThunk('partner/uploadAvatar', async (file, t) => {
  const token = gt(t);
  const fd = new FormData();
  fd.append('avatar', file);
  const r = await fetch(`${API}/api/partner/profile/avatar`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'), Authorization: token ? `Bearer ${token}` : undefined },
    body: fd,
  });
  const d = await r.json();
  if (!r.ok) return t.rejectWithValue(d.error);
  // Re-fetch profile to get updated avatarDataUrl
  const r2 = await fetch(`${API}/api/partner/profile`, { credentials: 'include', headers: hdr(token) });
  const d2 = await r2.json();
  return d2;
});

// ── Quests ──
export const fetchPartnerQuests = createAsyncThunk('partner/quests', async (_, t) => {
  const r = await fetch(`${API}/api/partner/quests`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const createPartnerQuest = createAsyncThunk('partner/createQuest', async (body, t) => {
  const r = await fetch(`${API}/api/partner/quests`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const updatePartnerQuest = createAsyncThunk('partner/updateQuest', async ({ id, updates }, t) => {
  const r = await fetch(`${API}/api/partner/quests/${id}`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(updates) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const deletePartnerQuest = createAsyncThunk('partner/deleteQuest', async (id, t) => {
  const r = await fetch(`${API}/api/partner/quests/${id}`, { method: 'DELETE', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

// ── Dashboard ──
export const fetchPartnerDashboard = createAsyncThunk('partner/dashboard', async (_, t) => {
  const r = await fetch(`${API}/api/partner/dashboard`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchDashboardTrippers = createAsyncThunk('partner/dashTrippers', async (_, t) => {
  const r = await fetch(`${API}/api/partner/dashboard/trippers`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Followers ──
export const fetchFollowers = createAsyncThunk('partner/followers', async (params = {}, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/partner/connections/followers?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchFollowerCount = createAsyncThunk('partner/followerCount', async (_, t) => {
  const r = await fetch(`${API}/api/partner/connections/followers/count`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Following (tripper side) ──
export const fetchFollowing = createAsyncThunk('partner/following', async (_, t) => {
  const r = await fetch(`${API}/api/partner/connections/following`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const followPartner = createAsyncThunk('partner/follow', async (partnerId, t) => {
  const r = await fetch(`${API}/api/partner/connections/follow/${partnerId}`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, partnerId };
});

export const unfollowPartner = createAsyncThunk('partner/unfollow', async (partnerId, t) => {
  const r = await fetch(`${API}/api/partner/connections/unfollow/${partnerId}`, { method: 'DELETE', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, partnerId };
});

// ── Notifications ──
export const fetchPartnerNotifications = createAsyncThunk('partner/notifs', async (params = {}, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/partner/notifications?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchPartnerUnreadCount = createAsyncThunk('partner/unread', async (_, t) => {
  const r = await fetch(`${API}/api/partner/notifications/unread-count`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const markPartnerNotifRead = createAsyncThunk('partner/markRead', async (id, t) => {
  const r = await fetch(`${API}/api/partner/notifications/${id}/read`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { id };
});

export const markAllPartnerNotifsRead = createAsyncThunk('partner/markAllRead', async (_, t) => {
  const r = await fetch(`${API}/api/partner/notifications/mark-all-read`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Public ──
export const fetchPublicPartner = createAsyncThunk('partner/public', async (id, t) => {
  const r = await fetch(`${API}/api/partner/public/${id}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchPublicPartnerQuests = createAsyncThunk('partner/publicQuests', async (params = {}, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/partner/public/quests?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// Discover surface: lists approved + active partners (with active quest
// count per partner). A partner appears as soon as an admin approves them,
// even without any quests yet.
export const fetchPublicPartners = createAsyncThunk('partner/publicPartners', async (params = {}, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/partner/public/list?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Availability ──
export const fetchAvailability = createAsyncThunk('partner/availability', async (params = {}, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/partner/availability?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const createSlot = createAsyncThunk('partner/createSlot', async (body, t) => {
  const r = await fetch(`${API}/api/partner/availability`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const updateSlot = createAsyncThunk('partner/updateSlot', async ({ id, updates }, t) => {
  const r = await fetch(`${API}/api/partner/availability/${id}`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(updates) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const deleteSlot = createAsyncThunk('partner/deleteSlot', async (id, t) => {
  const r = await fetch(`${API}/api/partner/availability/${id}`, { method: 'DELETE', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const bulkCreateSlots = createAsyncThunk('partner/bulkSlots', async (body, t) => {
  const r = await fetch(`${API}/api/partner/availability/bulk`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchCalendar = createAsyncThunk('partner/calendar', async (params = {}, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/partner/availability/calendar?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Partner Bookings ──
export const fetchPartnerBookings = createAsyncThunk('partner/bookings', async (params = {}, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/partner/bookings?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const updateBookingStatus = createAsyncThunk('partner/updateBooking', async ({ id, status }, t) => {
  const r = await fetch(`${API}/api/partner/bookings/${id}`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ status }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchBookingStats = createAsyncThunk('partner/bookingStats', async (_, t) => {
  const r = await fetch(`${API}/api/partner/bookings/stats`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchUpcomingBookings = createAsyncThunk('partner/upcoming', async (_, t) => {
  const r = await fetch(`${API}/api/partner/bookings/upcoming`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Sponsorship ──
export const fetchSponsorshipTiers = createAsyncThunk('partner/spTiers', async (_, t) => {
  const r = await fetch(`${API}/api/partner/sponsorship/tiers`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const purchaseSponsorship = createAsyncThunk('partner/spPurchase', async (body, t) => {
  const r = await fetch(`${API}/api/partner/sponsorship/purchase`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchActiveSponsorships = createAsyncThunk('partner/spActive', async (_, t) => {
  const r = await fetch(`${API}/api/partner/sponsorship/active`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchSponsorshipHistory = createAsyncThunk('partner/spHistory', async (_, t) => {
  const r = await fetch(`${API}/api/partner/sponsorship/history`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const cancelSponsorship = createAsyncThunk('partner/spCancel', async (id, t) => {
  const r = await fetch(`${API}/api/partner/sponsorship/${id}`, { method: 'DELETE', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const fetchSponsorshipAnalytics = createAsyncThunk('partner/spAnalytics', async (_, t) => {
  const r = await fetch(`${API}/api/partner/sponsorship/analytics`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

// ── Slice ──
const partnerSlice = createSlice({
  name: 'partner',
  initialState: {
    profile: null, quests: [], dashboard: null, dashTrippers: [],
    followers: [], followersPagination: null, followerCount: 0,
    following: [], notifications: [], unreadCount: 0,
    publicPartner: null, publicQuests: [], publicQuestsPagination: null,
    publicPartners: [], publicPartnersPagination: null,
    availability: [], calendar: [], bookings: [], bookingsPagination: null,
    bookingStats: null, upcomingBookings: [],
    sponsorshipTiers: null, activeSponsorships: [], sponsorshipHistory: [],
    sponsorshipAnalytics: null,
    status: 'idle', actionStatus: 'idle', actionMessage: null, error: null,
  },
  reducers: {
    clearPartnerAction(s) { s.actionStatus = 'idle'; s.actionMessage = null; s.error = null; },
    clearPublicPartner(s) { s.publicPartner = null; },
  },
  extraReducers: b => { b
    .addCase(fetchPartnerProfile.pending, s => { s.status = 'loading'; })
    .addCase(fetchPartnerProfile.fulfilled, (s, a) => { s.status = 'succeeded'; s.profile = a.payload; })
    .addCase(fetchPartnerProfile.rejected, (s, a) => { s.status = 'failed'; s.error = a.payload; })

    .addCase(uploadPartnerAvatar.fulfilled, (s, a) => { s.profile = a.payload; s.actionStatus = 'succeeded'; s.actionMessage = 'Avatar updated'; })
    .addCase(uploadPartnerAvatar.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })

    .addCase(updatePartnerProfile.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; if (a.payload.partner) s.profile = { ...s.profile, ...a.payload.partner }; })

    .addCase(fetchPartnerQuests.fulfilled, (s, a) => { s.quests = a.payload; })
    .addCase(createPartnerQuest.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; if (a.payload.quest) s.quests.unshift(a.payload.quest); })
    .addCase(updatePartnerQuest.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(deletePartnerQuest.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.quests = s.quests.filter(q => q._id !== a.payload.id); })

    .addCase(fetchPartnerDashboard.fulfilled, (s, a) => { s.dashboard = a.payload; })
    .addCase(fetchDashboardTrippers.fulfilled, (s, a) => { s.dashTrippers = a.payload; })

    .addCase(fetchFollowers.fulfilled, (s, a) => { s.followers = a.payload.followers; s.followersPagination = a.payload.pagination; })
    .addCase(fetchFollowerCount.fulfilled, (s, a) => { s.followerCount = a.payload.count; })
    .addCase(fetchFollowing.fulfilled, (s, a) => { s.following = a.payload; })

    .addCase(followPartner.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(unfollowPartner.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })

    .addCase(fetchPartnerNotifications.fulfilled, (s, a) => { s.notifications = a.payload.notifications; s.unreadCount = a.payload.unreadCount; })
    .addCase(fetchPartnerUnreadCount.fulfilled, (s, a) => { s.unreadCount = a.payload.count; })
    .addCase(markPartnerNotifRead.fulfilled, (s, a) => { s.notifications = s.notifications.map(n => n._id === a.payload.id ? { ...n, read: true } : n); s.unreadCount = Math.max(0, s.unreadCount - 1); })
    .addCase(markAllPartnerNotifsRead.fulfilled, (s) => { s.notifications = s.notifications.map(n => ({ ...n, read: true })); s.unreadCount = 0; })

    .addCase(fetchPublicPartner.fulfilled, (s, a) => { s.publicPartner = a.payload; })
    .addCase(fetchPublicPartnerQuests.fulfilled, (s, a) => { s.publicQuests = a.payload.quests; s.publicQuestsPagination = a.payload.pagination; })
    .addCase(fetchPublicPartners.fulfilled, (s, a) => { s.publicPartners = a.payload.partners; s.publicPartnersPagination = a.payload.pagination; })

    // Availability
    .addCase(fetchAvailability.fulfilled, (s, a) => { s.availability = a.payload; })
    .addCase(createSlot.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(deleteSlot.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.availability = s.availability.filter(sl => sl._id !== a.payload.id); })
    .addCase(bulkCreateSlots.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(fetchCalendar.fulfilled, (s, a) => { s.calendar = a.payload; })

    // Bookings
    .addCase(fetchPartnerBookings.fulfilled, (s, a) => { s.bookings = a.payload.bookings; s.bookingsPagination = a.payload.pagination; })
    .addCase(updateBookingStatus.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(fetchBookingStats.fulfilled, (s, a) => { s.bookingStats = a.payload; })
    .addCase(fetchUpcomingBookings.fulfilled, (s, a) => { s.upcomingBookings = a.payload; })

    // Sponsorship
    .addCase(fetchSponsorshipTiers.fulfilled, (s, a) => { s.sponsorshipTiers = a.payload; })
    .addCase(purchaseSponsorship.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(purchaseSponsorship.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(fetchActiveSponsorships.fulfilled, (s, a) => { s.activeSponsorships = a.payload; })
    .addCase(fetchSponsorshipHistory.fulfilled, (s, a) => { s.sponsorshipHistory = a.payload; })
    .addCase(cancelSponsorship.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.activeSponsorships = s.activeSponsorships.filter(sp => sp._id !== a.payload.id); })
    .addCase(fetchSponsorshipAnalytics.fulfilled, (s, a) => { s.sponsorshipAnalytics = a.payload; });
  },
});

export const { clearPartnerAction, clearPublicPartner } = partnerSlice.actions;
export default partnerSlice.reducer;
